import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Building2,
  Heart,
  PlusCircle,
  Eye,
  Users,
  TrendingUp,
  CheckCircle2,
  Clock,
  AlertTriangle,
  MapPin,
  BarChart3,
  MessageSquare,
  Calendar,
  Edit2,
  Shield,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export default function Dashboard() {
  const { user, isOwner, updateRole } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingBookings, setPendingBookings] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        };

        // Fetch favorites
        const favResponse = await fetch(`${API_URL}/api/favorites`, {
          headers,
        });
        if (favResponse.ok) {
          const data = await favResponse.json();
          setFavorites(data.favorites || []);
        }

        // Fetch owner properties if owner
        if (isOwner) {
          const propResponse = await fetch(`${API_URL}/api/owner/properties`, {
            headers,
          });
          if (propResponse.ok) {
            const data = await propResponse.json();
            setProperties(data.properties || []);
          }
        }

        // Fetch verification status
        const verResponse = await fetch(`${API_URL}/api/verification/status`, {
          headers,
        });
        if (verResponse.ok) {
          const data = await verResponse.json();
          setVerificationStatus(data);
        }

        // Fetch pending bookings count (for owners)
        if (isOwner) {
          const bookingResponse = await fetch(`${API_URL}/api/bookings/owner`, {
            headers,
          });
          if (bookingResponse.ok) {
            const data = await bookingResponse.json();
            const pending = data.bookings?.filter(b => b.status === 'pending').length || 0;
            setPendingBookings(pending);
          }
        }
      } catch (error) {
        // Keep console.error for production error tracking
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOwner]);

  const handleBecomeOwner = async () => {
    try {
      await updateRole('owner');
      toast.success('You are now a property owner! You can start listing properties.');
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const stats = isOwner ? [
    {
      label: 'Total Properties',
      value: properties.length,
      icon: Building2,
      color: 'text-blue-500',
      bg: 'bg-blue-50',
    },
    {
      label: 'Active Listings',
      value: properties.filter(p => p.status === 'available').length,
      icon: CheckCircle2,
      color: 'text-emerald-500',
      bg: 'bg-emerald-50',
    },
    {
      label: 'Total Views',
      value: properties.reduce((sum, p) => sum + (p.view_count || 0), 0),
      icon: Eye,
      color: 'text-purple-500',
      bg: 'bg-purple-50',
    },
    {
      label: 'Saved Properties',
      value: favorites.length,
      icon: Heart,
      color: 'text-accent',
      bg: 'bg-red-50',
    },
  ] : [
    {
      label: 'Saved Properties',
      value: favorites.length,
      icon: Heart,
      color: 'text-accent',
      bg: 'bg-red-50',
    },
  ];

  const statusColors = {
    available: 'bg-emerald-100 text-emerald-700',
    reserved: 'bg-amber-100 text-amber-700',
    inactive: 'bg-slate-100 text-slate-700',
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <Navbar />

      <div className="pt-20 pb-12">
        <div className="rh-container">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-serif text-3xl md:text-4xl text-slate-900 mb-2">
              Welcome back, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-slate-500">
              {isOwner ? 'Manage your properties and track performance' : 'Find and save your favorite properties'}
            </p>
          </div>

          {/* Stats */}
          <div className={`grid gap-4 mb-8 ${isOwner ? 'md:grid-cols-4' : 'md:grid-cols-1'}`}>
            {stats.map((stat) => (
              <Card key={stat.label} className="border-0 shadow-soft">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">{stat.label}</p>
                      <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 ${stat.bg} rounded-2xl flex items-center justify-center`}>
                      <stat.icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Quick Actions */}
          {isOwner && (
            <div className="mb-8 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
              <Card className="border-0 shadow-soft">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <BarChart3 className="w-5 h-5 text-blue-500" />
                  </div>
                  <h3 className="font-medium text-slate-900 mb-2">Analytics</h3>
                  <p className="text-xs text-slate-500 mb-4">View performance metrics</p>
                  <Button
                    onClick={() => navigate('/analytics')}
                    size="sm"
                    className="w-full bg-blue-500 hover:bg-blue-600 rounded-lg"
                  >
                    View Analytics
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-soft">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Calendar className="w-5 h-5 text-amber-500" />
                    {pendingBookings > 0 && (
                      <Badge className="bg-amber-500 text-white">{pendingBookings}</Badge>
                    )}
                  </div>
                  <h3 className="font-medium text-slate-900 mb-2">Bookings</h3>
                  <p className="text-xs text-slate-500 mb-4">Manage reservations</p>
                  <Button
                    onClick={() => navigate('/bookings')}
                    size="sm"
                    className="w-full bg-amber-500 hover:bg-amber-600 rounded-lg"
                  >
                    View Bookings
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-soft">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <MessageSquare className="w-5 h-5 text-cyan-500" />
                    {unreadMessages > 0 && (
                      <Badge className="bg-cyan-500 text-white">{unreadMessages}</Badge>
                    )}
                  </div>
                  <h3 className="font-medium text-slate-900 mb-2">Messages</h3>
                  <p className="text-xs text-slate-500 mb-4">Chat with tenants</p>
                  <Button
                    onClick={() => navigate('/messages')}
                    size="sm"
                    className="w-full bg-cyan-500 hover:bg-cyan-600 rounded-lg"
                  >
                    Open Messages
                  </Button>
                </CardContent>
              </Card>

              {!user?.is_verified_owner && (
                <Card className="border-0 shadow-soft bg-gradient-to-br from-emerald-50 to-teal-50">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <Shield className="w-5 h-5 text-emerald-600" />
                    </div>
                    <h3 className="font-medium text-slate-900 mb-2">Get Verified</h3>
                    <p className="text-xs text-slate-500 mb-4">Build trust with renters</p>
                    <Button
                      onClick={() => navigate('/verification')}
                      size="sm"
                      className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-lg"
                    >
                      Start Verification
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Become Owner CTA */}
          {!isOwner && (
            <Card className="mb-8 border-0 shadow-soft bg-gradient-to-r from-slate-900 to-slate-800 text-white">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div>
                    <h2 className="font-serif text-2xl font-bold mb-2">
                      Have a property to rent?
                    </h2>
                    <p className="text-slate-300">
                      Become a property owner and list your properties for free. Connect directly with renters.
                    </p>
                  </div>
                  <Button
                    onClick={handleBecomeOwner}
                    className="bg-accent hover:bg-accent/90 rounded-full px-8"
                    data-testid="become-owner-btn"
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    Become an Owner
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Main Content */}
          <Tabs defaultValue={isOwner ? 'properties' : 'favorites'} className="space-y-6">
            <TabsList className="bg-white p-1 rounded-full shadow-sm">
              {isOwner && (
                <TabsTrigger value="properties" className="rounded-full" data-testid="tab-properties">
                  My Properties
                </TabsTrigger>
              )}
              <TabsTrigger value="favorites" className="rounded-full" data-testid="tab-favorites">
                Saved Properties
              </TabsTrigger>
              {isOwner && (
                <TabsTrigger value="verification" className="rounded-full" data-testid="tab-verification">
                  Verification
                </TabsTrigger>
              )}
            </TabsList>

            {/* Properties Tab */}
            {isOwner && (
              <TabsContent value="properties">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-serif text-2xl text-slate-900">Your Properties</h2>
                  <Button
                    onClick={() => navigate('/property/new')}
                    className="bg-accent hover:bg-accent/90 rounded-full"
                    data-testid="add-property-btn"
                  >
                    <PlusCircle className="w-4 h-4 mr-2" />
                    Add Property
                  </Button>
                </div>

                {loading ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                      <Card key={i} className="animate-pulse">
                        <div className="h-48 bg-slate-200 rounded-t-xl" />
                        <CardContent className="p-4 space-y-2">
                          <div className="h-6 bg-slate-200 rounded w-3/4" />
                          <div className="h-4 bg-slate-200 rounded w-1/2" />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : properties.length === 0 ? (
                  <Card className="border-dashed border-2 border-slate-200">
                    <CardContent className="p-12 text-center">
                      <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <h3 className="font-serif text-xl text-slate-900 mb-2">No properties yet</h3>
                      <p className="text-slate-500 mb-6">Start by adding your first property listing</p>
                      <Button
                        onClick={() => navigate('/property/new')}
                        className="bg-accent hover:bg-accent/90 rounded-full"
                      >
                        <PlusCircle className="w-4 h-4 mr-2" />
                        Add Your First Property
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {properties.map((property) => (
                      <Card
                        key={property.property_id}
                        className="border-0 shadow-soft overflow-hidden hover:shadow-lg transition-shadow"
                        data-testid={`owner-property-${property.property_id}`}
                      >
                        <div className="relative h-48 cursor-pointer" onClick={() => navigate(`/property/${property.property_id}`)}>
                          <img
                            src={property.media?.[0]?.url || 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=800&q=80'}
                            alt={property.title}
                            className="w-full h-full object-cover"
                          />
                          <Badge className={`absolute top-3 right-3 ${statusColors[property.status]}`}>
                            {property.status.charAt(0).toUpperCase() + property.status.slice(1)}
                          </Badge>
                        </div>
                        <CardContent className="p-4">
                          <h3 className="font-serif text-lg font-bold text-slate-900 mb-1 line-clamp-1 cursor-pointer hover:text-accent" onClick={() => navigate(`/property/${property.property_id}`)}>
                            {property.title}
                          </h3>
                          <p className="text-sm text-slate-500 mb-3 flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {property.location?.city || 'Location not set'}
                          </p>
                          <div className="flex items-center justify-between mb-4">
                            <span className="font-bold text-slate-900">
                              ${property.monthly_rent}/mo
                            </span>
                            <span className="text-sm text-slate-500 flex items-center gap-1">
                              <Eye className="w-4 h-4" />
                              {property.view_count || 0} views
                            </span>
                          </div>
                          <Button
                            onClick={() => navigate(`/property/${property.property_id}/edit`)}
                            size="sm"
                            variant="outline"
                            className="w-full rounded-lg"
                          >
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit Property
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            )}

            {/* Favorites Tab */}
            <TabsContent value="favorites">
              <h2 className="font-serif text-2xl text-slate-900 mb-6">Saved Properties</h2>

              {loading ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3].map((i) => (
                    <Card key={i} className="animate-pulse">
                      <div className="h-48 bg-slate-200 rounded-t-xl" />
                      <CardContent className="p-4 space-y-2">
                        <div className="h-6 bg-slate-200 rounded w-3/4" />
                        <div className="h-4 bg-slate-200 rounded w-1/2" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : favorites.length === 0 ? (
                <Card className="border-dashed border-2 border-slate-200">
                  <CardContent className="p-12 text-center">
                    <Heart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-serif text-xl text-slate-900 mb-2">No saved properties</h3>
                    <p className="text-slate-500 mb-6">Start exploring and save properties you like</p>
                    <Button
                      onClick={() => navigate('/search')}
                      className="bg-slate-900 hover:bg-slate-800 rounded-full"
                    >
                      Explore Properties
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {favorites.map((property) => (
                    <Card
                      key={property.property_id}
                      className="border-0 shadow-soft overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => navigate(`/property/${property.property_id}`)}
                      data-testid={`favorite-${property.property_id}`}
                    >
                      <div className="relative h-48">
                        <img
                          src={property.media?.[0]?.url || 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=800&q=80'}
                          alt={property.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-serif text-lg font-bold text-slate-900 mb-1 line-clamp-1">
                          {property.title}
                        </h3>
                        <p className="text-sm text-slate-500 mb-3 flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {property.location?.city || 'Location not set'}
                        </p>
                        <span className="font-bold text-slate-900">
                          ${property.monthly_rent}/mo
                        </span>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Verification Tab */}
            {isOwner && (
              <TabsContent value="verification">
                <h2 className="font-serif text-2xl text-slate-900 mb-6">Owner Verification</h2>

                {user?.is_verified_owner ? (
                  <Card className="border-0 shadow-soft bg-emerald-50">
                    <CardContent className="p-8">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center">
                          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="font-serif text-xl font-bold text-emerald-900">
                            Verified Owner
                          </h3>
                          <p className="text-emerald-700">
                            Your identity has been verified. Your listings show the verified badge.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : verificationStatus?.verification?.status === 'pending' ? (
                  <Card className="border-0 shadow-soft bg-amber-50">
                    <CardContent className="p-8">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center">
                          <Clock className="w-8 h-8 text-amber-600" />
                        </div>
                        <div>
                          <h3 className="font-serif text-xl font-bold text-amber-900">
                            Verification Pending
                          </h3>
                          <p className="text-amber-700">
                            Your verification request is being reviewed. This usually takes 1-2 business days.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="border-0 shadow-soft">
                    <CardContent className="p-8">
                      <div className="max-w-lg">
                        <h3 className="font-serif text-xl font-bold text-slate-900 mb-4">
                          Get Verified
                        </h3>
                        <p className="text-slate-600 mb-6">
                          Verified owners get a trust badge on their listings, increasing credibility with potential renters.
                        </p>
                        <div className="space-y-3 mb-6">
                          <div className="flex items-center gap-3">
                            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                            <span className="text-slate-700">Upload a valid ID document</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                            <span className="text-slate-700">Take a selfie for verification</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                            <span className="text-slate-700">Get verified within 1-2 days</span>
                          </div>
                        </div>
                        <Button
                          className="bg-accent hover:bg-accent/90 rounded-full"
                          data-testid="start-verification-btn"
                        >
                          Start Verification
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
    </div>
  );
}
